<script setup lang="ts">
import { WidgetBreadcrumbs } from '~/components/widget/breadcrumbs'
import type { ICurrencyRate } from '~/components/widget/rates'
import { WidgetRatesCurrencyTable } from '~/components/widget/rates'
import { WidgetSeoContent } from '~/components/widget/seo'

const route = useRoute()
const requestUrl = useRequestURL()
const baseUrl = requestUrl.origin
const currency = route.params.currency
const action: 'buy' | 'sell' = (route.name as string)?.includes('sell') ? 'sell' : 'buy'

const currencyStore = useCurrencyStore()
await currencyStore.fetchCurrencies()
const { list } = storeToRefs(currencyStore)

const selectedCurrency = computed(() =>
  list.value.find((i) => i.code.toLowerCase() === (currency as string).toLowerCase())
)

const seoCurrency = computed(() => ({
  currency: {
    title: selectedCurrency.value?.title,
    description: selectedCurrency.value?.description,
    date: useDateFormat(new Date().toISOString())
  }
}))

const { t, locale } = useI18n()

const operationTitle = computed(() => {
  return action === 'buy' ? t('common.buy') : t('common.sell')
})

const actionUrl = computed(() => {
  return `/rates/${currency}/${action}`
})

useSeoFromCustom(seoCurrency, baseUrl, locale.value)

const breadcrumbs = computed(() => [
  { title: t('common.exchange_rates'), url: '/rates' },
  { title: selectedCurrency.value?.title || '', url: `/rates/${currency}` },
  { title: operationTitle.value, url: actionUrl.value }
])
</script>

<template>
  <Main>
    <Container>
      <WidgetBreadcrumbs
        :links="breadcrumbs"
        :variables="{ currency: selectedCurrency?.title || '' }"
      />
      <div class="grid grid-cols-1 gap-5 xl:grid-cols-4">
        <div class="space-y-5 xl:col-span-4">
          <WidgetSeoContent
            position="upper"
            :variables="{
              currency: selectedCurrency?.title || '',
              date: useDateFormat(new Date().toISOString())
            }"
          />
          <ContentBlock variant="white">
            <WidgetRatesCurrencyTable
              :currency="currency as keyof ICurrencyRate"
              :action="action"
            />
          </ContentBlock>
          <WidgetSeoContent
            position="bottom"
            :variables="{
              currency: selectedCurrency?.title || '',
              date: useDateFormat(new Date().toISOString())
            }"
          />
        </div>
      </div>
    </Container>
  </Main>
</template>
